import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import { submitLead, packFormFields } from '../lib/submitLead';
import { useLeadFormGuard } from '../hooks/useLeadFormGuard';
import { HoneypotField } from '../components/forms/HoneypotField';
import './FactFinderPage.css';

type SubmitState = 'idle' | 'loading' | 'success' | 'error';

const META_DESCRIPTION =
  'Fact Finder is a credentialed-access AI session with a digital twin of your judge, arbitrator, or tribunal. Built from the decision maker’s own published rulings, opinions, and arbitral awards. Pressure test motions, experts, and framing before positions lock in.';
const META_KEYWORDS =
  'judicial analytics, arbitrator analytics, judge research, arbitration research, motion preparation, Daubert, summary judgment, Markman, class certification, motions in limine, three-arbitrator tribunal, appellate panel, final award framing, AAA, JAMS, CPR, ICC, ICDR, LCIA, SIAC, HKIAC, UNCITRAL, legal AI, litigation strategy';

function setMeta(name: string, content: string, attr: 'name' | 'property' = 'name') {
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

const FactFinderPage: React.FC = () => {
  usePageTitle('Fact Finder | AI Judge and Arbitrator Modeling for Trial Counsel');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    firm: '',
    phone: '',
    role: '',
    matter: '',
  });
  const { honeypot, formProps, getGuardFields } = useLeadFormGuard();
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const prevDesc = document.querySelector('meta[name="description"]')?.getAttribute('content') ?? '';
    const prevKeys = document.querySelector('meta[name="keywords"]')?.getAttribute('content') ?? '';
    const prevOgTitle = document.querySelector('meta[property="og:title"]')?.getAttribute('content') ?? '';
    const prevOgDesc = document.querySelector('meta[property="og:description"]')?.getAttribute('content') ?? '';
    const prevOgType = document.querySelector('meta[property="og:type"]')?.getAttribute('content') ?? '';
    setMeta('description', META_DESCRIPTION);
    setMeta('keywords', META_KEYWORDS);
    setMeta('og:title', 'Fact Finder | AI Judge and Arbitrator Modeling', 'property');
    setMeta('og:description', META_DESCRIPTION, 'property');
    setMeta('og:type', 'product', 'property');
    return () => {
      if (prevDesc) setMeta('description', prevDesc);
      if (prevKeys) setMeta('keywords', prevKeys);
      if (prevOgTitle) setMeta('og:title', prevOgTitle, 'property');
      if (prevOgDesc) setMeta('og:description', prevOgDesc, 'property');
      if (prevOgType) setMeta('og:type', prevOgType, 'property');
    };
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitState('loading');
    setErrorMessage('');

    const packedMessage = packFormFields({
      requestType: 'fact-finder-access',
      firm: formData.firm,
      phone: formData.phone,
      role: formData.role,
      matter: formData.matter,
      source: 'fact-finder-page',
    });

    const result = await submitLead({
      name: formData.name,
      email: formData.email,
      message: packedMessage,
      ...getGuardFields(),
    });

    if (result.success) {
      setSubmitState('success');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setSubmitState('error');
      setErrorMessage(result.error || 'Failed to submit. Please try again.');
    }
  };

  /* CONFIRMATION: replace the whole page when submitted */
  if (submitState === 'success') {
    return (
      <div className="ff-page">
        <section className="ff-confirm">
          <div className="ff-hero__atmosphere" aria-hidden="true">
            <span className="ff-hero__grid" />
            <span className="ff-hero__glow" />
          </div>
          <div className="container ff-confirm__inner">
            <div className="ff-confirm__seal" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="5 13 10 18 20 7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <span className="ff-eyebrow">Fact Finder<sup>™</sup></span>
            <h1 className="ff-confirm__title">
              Request received.
              <span className="ff-confirm__title-accent">We&rsquo;ll take it from here.</span>
            </h1>
            {/* Deliberately does not name the address we reply from.
                Publishing it on a public confirmation page hands
                spoofers a ready-made From: to imitate, and ties us to
                one mailbox we'd have to keep forever. */}
            <p className="ff-confirm__lead">
              A member of our team will verify your credentials and follow up by
              email within one business day. We typically respond same-day during
              business hours.
            </p>
            <ol className="ff-confirm__steps">
              <li>
                <span className="ff-confirm__step-num">01</span>
                <div>
                  <strong>Credentials verified.</strong>
                  We confirm your bar admission, firm affiliation, and counsel-of-record
                  status on the matter.
                </div>
              </li>
              <li>
                <span className="ff-confirm__step-num">02</span>
                <div>
                  <strong>Coverage confirmed.</strong>
                  We check whether your bench or tribunal is already modeled, or build
                  the fact finder within 72 hours.
                </div>
              </li>
              <li>
                <span className="ff-confirm__step-num">03</span>
                <div>
                  <strong>Session opened.</strong>
                  You get a sandboxed access link and a short onboarding so you can run
                  the matter through the model before the next filing.
                </div>
              </li>
            </ol>
            <div className="ff-confirm__actions">
              <Link to="/" className="ff-confirm__home">Back to JuryView</Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="ff-page">
      {/* HERO: split layout, form is the CTA */}
      <section className="ff-hero">
        <div className="ff-hero__atmosphere" aria-hidden="true">
          <span className="ff-hero__grid" />
          <span className="ff-hero__glow" />
        </div>

        <div className="container ff-hero__layout">
          {/* LEFT: value prop */}
          <div className="ff-hero__copy">
            <span className="ff-eyebrow">Fact Finder<sup>™</sup></span>

            <h1 className="ff-hero__title">
              Run it past the bench.
              <span className="ff-hero__title-accent">Before positions get locked in.</span>
            </h1>

            <p className="ff-hero__lead">
              Fact Finder is a live session with a digital twin of your judge, arbitrator,
              or tribunal built from the decision maker's own written reasoning and
              motion history.
            </p>
            <p className="ff-hero__lead ff-hero__lead--sub">
              The system evolved out of our synthetic juror work, modeling how fact
              finders process credibility, framing, evidentiary tension, and argumentative
              structure under real litigation pressure.
            </p>
            <p className="ff-hero__lead ff-hero__lead--punch">
              Pressure test arguments, experts, themes, and strategic framing against the
              actual decision maker before they are committed in court.
            </p>

            <ul className="ff-hero__trust">
              <li>Credentialed access only</li>
              <li>Sandboxed per counsel</li>
              <li>No public rating, ranking, or scoring</li>
            </ul>
          </div>

          {/* RIGHT: form */}
          <aside className="ff-form" aria-labelledby="ff-form-title">
            <div className="ff-form__head">
              <h2 id="ff-form-title">Request access</h2>
              <p>Tell us the matter. We'll open the session.</p>
            </div>

            <form className="ff-form__fields" onSubmit={handleSubmit} {...formProps}>
              <HoneypotField {...honeypot} />
              <div className="ff-field">
                <label htmlFor="ff-name">Full name</label>
                <input
                  id="ff-name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Andrea Liu"
                  required
                />
              </div>
              <div className="ff-field">
                <label htmlFor="ff-email">Work email</label>
                <input
                  id="ff-email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="aliu@firm.com"
                  required
                />
              </div>
              <div className="ff-field">
                <label htmlFor="ff-firm">Firm or organization</label>
                <input
                  id="ff-firm"
                  name="firm"
                  type="text"
                  value={formData.firm}
                  onChange={handleChange}
                  placeholder="Liu &amp; Partners LLP"
                  required
                />
              </div>
              <div className="ff-field">
                <label htmlFor="ff-phone">Phone</label>
                <input
                  id="ff-phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(555) 555-0199"
                  required
                />
              </div>
              <div className="ff-field">
                <label htmlFor="ff-role">Role</label>
                <select
                  id="ff-role"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  required
                >
                  <option value="" disabled>Select…</option>
                  <option value="Counsel, litigation">Counsel, litigation</option>
                  <option value="Counsel, arbitration">Counsel, arbitration</option>
                  <option value="Sitting arbitrator">Sitting arbitrator</option>
                  <option value="In-house / general counsel">In-house / general counsel</option>
                  <option value="Trial consultant">Trial consultant</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="ff-field">
                <label htmlFor="ff-matter">Matter and the fact finder you are preparing for</label>
                <textarea
                  id="ff-matter"
                  name="matter"
                  rows={3}
                  value={formData.matter}
                  onChange={handleChange}
                  placeholder="A line or two on stage, the bench or tribunal, and any timing pressure."
                  required
                />
              </div>

              {submitState === 'error' && (
                <div className="ff-form__error" role="alert">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" strokeLinecap="round" />
                    <line x1="12" y1="16" x2="12.01" y2="16" strokeLinecap="round" />
                  </svg>
                  <span>{errorMessage}</span>
                </div>
              )}

              <button
                type="submit"
                className={`ff-cta ${submitState === 'loading' ? 'is-loading' : ''}`}
                disabled={submitState === 'loading'}
              >
                {submitState === 'loading' ? (
                  <>
                    <span className="ff-cta__spinner" aria-hidden="true" />
                    Submitting…
                  </>
                ) : (
                  <>
                    Request access
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                      <path d="m9 6 6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </>
                )}
              </button>

              <p className="ff-form__fine">
                Submitting confirms you are counsel of record, or retained by counsel of
                record, on the matter described.
              </p>
            </form>
          </aside>
        </div>
      </section>

      {/* PILLARS */}
      <section className="ff-pillars" aria-labelledby="ff-pillars-title">
        <div className="container">
          <h2 id="ff-pillars-title" className="ff-section-h">What a Fact Finder session does</h2>
          <div className="ff-pillars__grid">
            <article className="ff-pillar">
              <span className="ff-pillar__num">01</span>
              <h3>Test every framing.</h3>
              <p>
                Pose the hypothetical. Attach the motion. Walk through the argument.
                The model answers in posture, grounded in this bench&rsquo;s prior orders
                and reasoning.
              </p>
            </article>
            <article className="ff-pillar">
              <span className="ff-pillar__num">02</span>
              <h3>Stack the panel.</h3>
              <p>
                Group two or three fact finders into a tribunal or appellate panel.
                Watch them agree, split, or carry the room, and see which member moves
                the others.
              </p>
            </article>
            <article className="ff-pillar">
              <span className="ff-pillar__num">03</span>
              <h3>Cited, or it didn&rsquo;t happen.</h3>
              <p>
                Every position the model takes traces to a real opinion, order, or
                published award. When the bench has not ruled on a question, the model
                tells you so.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* SAMPLE SESSION MOCKUP */}
      <section className="ff-sample" aria-labelledby="ff-sample-title">
        <div className="container ff-sample__layout">
          <div className="ff-sample__intro">
            <h2 id="ff-sample-title" className="ff-section-h">A turn with the bench.</h2>
            <p>
              One question. The digital twin answers in the bench&rsquo;s voice, grounded
              in their actual prior orders. Fact Finder surfaces how the bench has
              decided and what they weigh. Counsel stays first chair.
            </p>
            <ul className="ff-sample__legend">
              <li>
                <span className="ff-sample__legend-tag ff-sample__legend-tag--posture">POSTURE</span>
                How the model frames your question. Stage, doctrinal posture, the kind of motion this is.
              </li>
              <li>
                <span className="ff-sample__legend-tag ff-sample__legend-tag--ruling">POSTURE CALL</span>
                The bench&rsquo;s read, in their voice. Set in serif to mark the authoritative line.
              </li>
              <li>
                <span className="ff-sample__legend-tag ff-sample__legend-tag--align">ALIGNMENT</span>
                How tightly the answer tracks the bench&rsquo;s own prior record. Below 60%, the model flags low confidence and tells you why.
              </li>
            </ul>
          </div>

          <div className="ff-mock" role="presentation">
            <div className="ff-mock__header">
              <div className="ff-mock__avatar" aria-hidden="true">ME</div>
              <div className="ff-mock__heading">
                <span className="ff-mock__name">Q&amp;A · Hon. M. Ellison</span>
                <span className="ff-mock__court">U.S. District · Sample · composite bench</span>
              </div>
              <span className="ff-mock__live" aria-label="Session ready, one turn">
                <span className="ff-mock__live-dot" /> READY · 1 turn
              </span>
            </div>

            <div className="ff-mock__panelbar">
              <span className="ff-mock__panel-label">CORPUS</span>
              <span className="ff-mock__meta">1,247 rulings indexed</span>
              <span className="ff-mock__meta-divider" aria-hidden="true">·</span>
              <span className="ff-mock__meta">refreshed 48 hrs ago</span>
              <span className="ff-mock__chip ff-mock__chip--ghost">+ Add panelist</span>
            </div>

            <div className="ff-mock__stream">
              <div className="ff-mock__qrow">
                <span className="ff-mock__role">Counsel</span>
                <div className="ff-mock__qbubble">
                  On Rule 37(e) spoliation, where ESI was lost through routine
                  retention or auto-delete and prejudice is contested but not
                  catastrophic, does the bench tilt toward adverse inference, lesser
                  sanctions, or denial?
                </div>
              </div>

              <div className="ff-mock__arow">
                <span className="ff-mock__role">In the bench&rsquo;s voice</span>

                <article className="ff-mock__card">
                  <div className="ff-mock__cardhead">
                    <span className="ff-mock__tag">POSTURE CALL</span>
                    <span className="ff-mock__corpus">
                      <span className="ff-mock__corpus-dot" /> Drawn from analogous prior rulings
                    </span>
                  </div>

                  <div className="ff-mock__section">
                    <span className="ff-mock__sectlabel">POSTURE</span>
                    <p>
                      Pre-trial. Rule 37(e) spoliation motion. ESI loss via routine
                      retention or auto-delete. Prejudice contested. No allegation of
                      intent to deprive.
                    </p>
                  </div>

                  <h3 className="ff-mock__ruling">
                    &ldquo;Without intent to deprive, I&rsquo;m not giving you the
                    adverse inference. You&rsquo;ll get something, usually a curative
                    instruction or limited fee-shifting, but 37(e)(2) is off the table
                    on this record.&rdquo;
                  </h3>

                  <div className="ff-mock__body">
                    <p>
                      Rule 37(e)(1) versus (e)(2) is a line I draw strictly. Negligent
                      loss of ESI, even where prejudice is real, gets you to (e)(1).
                      For (e)(2) and an adverse inference, I need intent to deprive,
                      not sloppy retention.
                    </p>
                    <p>
                      I denied the (e)(2) motion in <em>Order on Spoliation, 2023</em>{' '}
                      where the auto-delete ran routinely and there was no
                      preservation-hold violation traceable to bad faith. I granted a
                      curative instruction and limited fee-shifting under (e)(1). The
                      court of appeals affirmed.
                    </p>
                  </div>

                  <div className="ff-mock__weighs">
                    <span className="ff-mock__weighs-label">What this bench weighs</span>
                    <ul>
                      <li>Separation between the negligence story and the intent story on the record.</li>
                      <li>Specificity about what was lost and what is now unprovable because of it.</li>
                      <li>The retention policy itself, and timing of the litigation hold against the trigger event.</li>
                    </ul>
                  </div>

                  <div className="ff-mock__cardfoot">
                    <div className="ff-mock__align">
                      <span className="ff-mock__sectlabel">Alignment with prior rulings</span>
                      <div className="ff-mock__bar">
                        <span style={{ width: '84%' }} />
                      </div>
                      <span className="ff-mock__pct">84% favorable</span>
                    </div>
                    <div className="ff-mock__cites">
                      <span className="ff-mock__sectlabel">From the bench&rsquo;s own record</span>
                      <span className="ff-mock__cite">Order on Spoliation, 2023</span>
                      <span className="ff-mock__cite">Memo &amp; Order on Rule 37 sanctions, 2022</span>
                      <span className="ff-mock__cite">Order on adverse inference, 2020</span>
                    </div>
                  </div>
                </article>
              </div>
            </div>

            <div className="ff-mock__composer" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21.4 11.6 12.6 2.8a2 2 0 0 0-2.8 0L2.8 9.8a2 2 0 0 0 0 2.8L11.6 21.4a2 2 0 0 0 2.8 0l7-7a2 2 0 0 0 0-2.8Z" strokeLinejoin="round" />
                <circle cx="8.5" cy="8.5" r="1.2" fill="currentColor" stroke="none" />
              </svg>
              <span className="ff-mock__placeholder">Push back. Attach the brief. Ask a follow-up.</span>
              <button type="button" className="ff-mock__send" tabIndex={-1}>
                Send
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="m9 6 6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>

            <div className="ff-mock__foot">
              <span>ANALYTIC TWIN</span>
              <span aria-hidden="true">·</span>
              <span>NOT LEGAL ADVICE</span>
            </div>
          </div>
        </div>
      </section>

      {/* PRIVILEGE & SECURITY: answers the first question a partner asks */}
      <section className="ff-privacy" aria-labelledby="ff-privacy-title">
        <div className="ff-privacy__atmosphere" aria-hidden="true">
          <span className="ff-privacy__grid" />
          <span className="ff-privacy__glow" />
        </div>
        <div className="container ff-privacy__inner">
          <div className="ff-privacy__intro">
            <span className="ff-privacy__eyebrow">Privilege &amp; Security</span>
            <h2 id="ff-privacy-title" className="ff-privacy__title">
              Built for privileged matter.<br />
              <span className="ff-privacy__title-accent">Every assumption you should have, named.</span>
            </h2>
            <p className="ff-privacy__lead">
              Where your session data lives, who can see it, and what we will never do
              with your work.
            </p>
          </div>

          <div className="ff-privacy__grid-items">
            <article className="ff-priv-item">
              <span className="ff-priv-item__icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                  <path d="M12 3 4 6v6c0 5 3.5 8.5 8 9 4.5-.5 8-4 8-9V6l-8-3Z" strokeLinejoin="round" />
                  <path d="m9 12 2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              <div className="ff-priv-item__body">
                <h3>Privilege-respecting by design.</h3>
                <p>
                  Sessions are sandboxed per credentialed user. Your attachments and
                  questions are not visible to other firms, other clients, or our staff
                  outside an audited support need.
                </p>
              </div>
            </article>

            <article className="ff-priv-item">
              <span className="ff-priv-item__icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                  <rect x="4" y="10" width="16" height="11" rx="2" />
                  <path d="M8 10V7a4 4 0 0 1 8 0v3" strokeLinecap="round" />
                  <path d="M10 14h4" strokeLinecap="round" />
                </svg>
              </span>
              <div className="ff-priv-item__body">
                <h3>No training on your work.</h3>
                <p>
                  Your matter content, attached briefs, and session text are not used to
                  train any model, ours or any third party&rsquo;s. Ever.
                </p>
              </div>
            </article>

            <article className="ff-priv-item">
              <span className="ff-priv-item__icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                  <rect x="3" y="4" width="18" height="6" rx="1.5" />
                  <rect x="3" y="14" width="18" height="6" rx="1.5" />
                  <path d="M7 7h.01M7 17h.01" strokeLinecap="round" />
                </svg>
              </span>
              <div className="ff-priv-item__body">
                <h3>U.S. data residency.</h3>
                <p>
                  Session data lives on infrastructure operated in the United States,
                  encrypted at rest and in transit. No cross-border replication without
                  explicit counsel consent.
                </p>
              </div>
            </article>

            <article className="ff-priv-item">
              <span className="ff-priv-item__icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                  <path d="M6 3h9l4 4v14H6z" strokeLinejoin="round" />
                  <path d="M14 3v5h5" strokeLinejoin="round" />
                  <path d="M9 13h7M9 17h5" strokeLinecap="round" />
                </svg>
              </span>
              <div className="ff-priv-item__body">
                <h3>Subpoena protocol.</h3>
                <p>
                  Compelled disclosure requires a court order. Where law permits, we
                  notify counsel before responding so privilege and work-product
                  protections can be asserted.
                </p>
              </div>
            </article>

            <article className="ff-priv-item">
              <span className="ff-priv-item__icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                  <path d="M12 4v8" strokeLinecap="round" />
                  <path d="M8 8l4-4 4 4" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M4 13v5a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5" strokeLinejoin="round" />
                </svg>
              </span>
              <div className="ff-priv-item__body">
                <h3>Retention you control.</h3>
                <p>
                  Sessions are retained for the active matter and purged on request. You
                  can also wipe a session manually at any time from inside the workspace.
                </p>
              </div>
            </article>

            <article className="ff-priv-item">
              <span className="ff-priv-item__icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                  <path d="M12 3v18" strokeLinecap="round" />
                  <path d="M5 8h14" strokeLinecap="round" />
                  <path d="M5 8l-2 6a4 4 0 0 0 8 0L9 8" strokeLinejoin="round" />
                  <path d="M19 8l-2 6a4 4 0 0 0 8 0l-2-6" strokeLinejoin="round" />
                </svg>
              </span>
              <div className="ff-priv-item__body">
                <h3>Ethics-aware development.</h3>
                <p>
                  Designed in consultation with practicing trial counsel, evidentiary
                  specialists, and bar-rule-aware litigation strategists. Bar opinions
                  referenced in development are available to credentialed counsel on
                  request.
                </p>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* COVERAGE + USE */}
      <section className="ff-info">
        <div className="container ff-info__grid">
          <article className="ff-info__col">
            <h2 className="ff-info__title">Coverage</h2>
            <p>
              U.S. federal and state judiciary at both trial and appellate levels.
              Arbitration coverage includes AAA, JAMS, CPR, ICC, ICDR, LCIA, SIAC, HKIAC,
              UNCITRAL, and ad hoc tribunals. Each fact finder is refreshed against its
              bench or tribunal&rsquo;s most recent published decisional record.
            </p>
          </article>

          <article className="ff-info__col">
            <h2 className="ff-info__title">Where counsel use it</h2>
            <p>
              Daubert challenges, summary judgment briefing, motions in limine, Markman
              hearings, class certification, preliminary injunction practice, bench trial
              preparation, final award framing, three-arbitrator tribunal calibration,
              and appellate panel rehearsal.
            </p>
          </article>
        </div>
      </section>

      {/* FAQ: knocks down remaining objections before the form */}
      <section className="ff-faq" aria-labelledby="ff-faq-title">
        <div className="container ff-faq__inner">
          <div className="ff-faq__intro">
            <span className="ff-faq__eyebrow">Common questions</span>
            <h2 id="ff-faq-title">Before you submit.</h2>
            <p>If your question isn&rsquo;t answered here, add it to the matter description below.</p>
          </div>
          <div className="ff-faq__list">
            <details className="ff-faq__item" open>
              <summary>
                <span>How accurate is the model?</span>
                <span className="ff-faq__chev" aria-hidden="true">+</span>
              </summary>
              <div className="ff-faq__answer">
                <p>
                  Each response includes an alignment score that measures how tightly
                  the answer tracks the bench&rsquo;s prior reasoning record. Below 60%
                  alignment, the model flags the response as low confidence and tells
                  you why. A validation summary by motion type is available to
                  credentialed counsel inside intake.
                </p>
              </div>
            </details>

            <details className="ff-faq__item">
              <summary>
                <span>Is my matter content private?</span>
                <span className="ff-faq__chev" aria-hidden="true">+</span>
              </summary>
              <div className="ff-faq__answer">
                <p>
                  Yes. Sessions are sandboxed per credentialed user. Your attachments
                  and questions are not visible to other firms, other clients, or our
                  staff outside an audited support need. Your work product is never
                  used to train any model. U.S. data residency, encrypted at rest and
                  in transit.
                </p>
              </div>
            </details>

            <details className="ff-faq__item">
              <summary>
                <span>How is this different from Lex Machina, Premonition, or Westlaw Edge?</span>
                <span className="ff-faq__chev" aria-hidden="true">+</span>
              </summary>
              <div className="ff-faq__answer">
                <p>
                  Analytics platforms surface what a bench has done. Fact Finder lets
                  you interrogate the reasoning behind it. The output is a posture in
                  the bench&rsquo;s voice, grounded in citations to their own prior
                  orders, not a stat sheet on grant rates.
                </p>
              </div>
            </details>

            <details className="ff-faq__item">
              <summary>
                <span>Is this consistent with bar ethics rules?</span>
                <span className="ff-faq__chev" aria-hidden="true">+</span>
              </summary>
              <div className="ff-faq__answer">
                <p>
                  Fact Finder is decision-support for credentialed counsel, not a public
                  rating service or a legal-advice tool. The product is designed in
                  consultation with practicing trial counsel and bar-rule-aware
                  litigation strategists. Counsel remains the pilot at every turn. Bar
                  opinions referenced in development are available on request.
                </p>
              </div>
            </details>

            <details className="ff-faq__item">
              <summary>
                <span>What if my judge or arbitrator isn&rsquo;t already modeled?</span>
                <span className="ff-faq__chev" aria-hidden="true">+</span>
              </summary>
              <div className="ff-faq__answer">
                <p>
                  Custom fact finders are built in 72 hours from a vetted counsel
                  intake. Tell us the matter, the bench or tribunal, and any procedural
                  timing inside the access request. We confirm coverage in intake, not
                  on the marketing page.
                </p>
              </div>
            </details>

            <details className="ff-faq__item">
              <summary>
                <span>What does access cost?</span>
                <span className="ff-faq__chev" aria-hidden="true">+</span>
              </summary>
              <div className="ff-faq__answer">
                <p>
                  Access is tiered by matter and counsel role, from single-matter
                  through firm-wide. Pricing is shared inside intake once we have your
                  role and case context, so we can match the access model to the
                  engagement.
                </p>
              </div>
            </details>
          </div>
        </div>
      </section>

      {/* FOOTNOTE */}
      <div className="ff-foot container">
        <p>
          Fact Finder is a strategic preparation tool for credentialed counsel. It is not
          a public rating service for the judiciary. Each fact finder is built solely from
          the bench&rsquo;s or tribunal&rsquo;s own published decisional record. We do not
          access non-public materials, and we do not solicit, infer, or synthesize the
          views of any judge or arbitrator. Fact Finder has no connection to, endorsement
          by, or affiliation with any judge, arbitrator, court, or tribunal.
        </p>
        <p className="ff-foot__links">
          <Link to="/legal">Terms</Link>
          <span aria-hidden="true">·</span>
          <Link to="/privacy">Privacy</Link>
        </p>
      </div>
    </div>
  );
};

export default FactFinderPage;
